import * as RadixTabs from "@radix-ui/react-tabs";
import styles from "./Tabs.module.scss";
import cx from "classnames";
import { useCallback, useState } from "react";
import Modal from "../Modal/Modal";
import ConfirmationModal from "../Modal/ConfirmationModal";

type Tab = {
  id: string;
  label: React.ReactNode;
  content?: React.ReactNode;
};

type TabsProps = {
  tabs: Tab[];
  defaultValue?: string;
  value?: string;
  onValueChange?: (value: string) => void;
  className?: string;
  classNames?: {
    root?: string;
    list?: string;
    content?: string;
  };
  blockIfDirty?: boolean;
  isDirty?: boolean;
};

function Tabs({
  defaultValue,
  tabs,
  value,
  onValueChange,
  className,
  classNames,
  blockIfDirty,
  isDirty,
}: TabsProps) {
  const [currentValue, setCurrentValue] = useState(defaultValue ?? value);
  const [nextValue, setNextValue] = useState(defaultValue ?? value);

  const isOpen = currentValue !== nextValue;

  const onProceed = useCallback(() => {
    setCurrentValue(nextValue);

    if (onValueChange) {
      onValueChange(nextValue ?? "");
    }
  }, [onValueChange, nextValue]);

  const onCancel = useCallback(() => {
    setNextValue(currentValue);

    if (onValueChange) {
      onValueChange(currentValue ?? "");
    }
  }, [currentValue, onValueChange]);

  return (
    <>
      <Modal
        isOpen={isOpen}
        onClose={onCancel}
        title="Discard changes?"
        subtitle="You have unsaved changes that will be discarded if you continue."
      >
        <ConfirmationModal
          onClose={onCancel}
          onSubmit={onProceed}
          confirmationText="Discard"
          destructive
        />
      </Modal>
      <RadixTabs.Root
        defaultValue={defaultValue}
        className={cx(styles.root, className, classNames?.root)}
        value={blockIfDirty ? currentValue : value}
        onValueChange={
          blockIfDirty
            ? (v) => {
                setNextValue(v);

                if (!isDirty) {
                  setCurrentValue(v);

                  if (onValueChange) {
                    onValueChange(v);
                  }
                }
              }
            : onValueChange
        }
      >
        <RadixTabs.List className={cx(styles.list, classNames?.list)}>
          {tabs.map(({ id, label }) => (
            <RadixTabs.Trigger key={id} value={id} className={styles.trigger}>
              {label}
            </RadixTabs.Trigger>
          ))}
        </RadixTabs.List>
        {tabs.map(({ id, content }) => (
          <RadixTabs.Content
            key={id}
            value={id}
            className={cx(styles.content, classNames?.content)}
          >
            {content}
          </RadixTabs.Content>
        ))}
      </RadixTabs.Root>
    </>
  );
}

export default Tabs;
